import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ExternalLink, Calendar, Users, Eye, Download, type LucideIcon } from 'lucide-react';
import { format } from 'date-fns';

interface SharedCalendar {
  id: string;
  slug: string;
  email: string;
  location_name: string;
  year: number;
  latitude: number;
  longitude: number;
  view_count: number;
  download_count: number;
  is_premium_user: boolean;
  created_at: string;
}

interface Stats {
  totalCalendars: number;
  uniqueCreators: number;
  totalViews: number;
  totalDownloads: number;
  premiumCalendars: number;
  freeCalendars: number;
}

export const CalendarAnalytics: React.FC = () => {
  const [calendars, setCalendars] = useState<SharedCalendar[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalCalendars: 0,
    uniqueCreators: 0,
    totalViews: 0,
    totalDownloads: 0,
    premiumCalendars: 0,
    freeCalendars: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'created_at' | 'view_count' | 'download_count'>('created_at');
  const [sortDesc, setSortDesc] = useState(true);

  const fetchCalendars = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('shared_calendars')
        .select('*')
        .order(sortBy, { ascending: !sortDesc });

      if (error) throw error;

      const calendarsData = data || [];
      setCalendars(calendarsData);

      // Calculate stats
      const uniqueEmails = new Set(calendarsData.map(c => c.email));
      setStats({
        totalCalendars: calendarsData.length,
        uniqueCreators: uniqueEmails.size,
        totalViews: calendarsData.reduce((sum, c) => sum + (c.view_count || 0), 0),
        totalDownloads: calendarsData.reduce((sum, c) => sum + (c.download_count || 0), 0),
        premiumCalendars: calendarsData.filter(c => c.is_premium_user).length,
        freeCalendars: calendarsData.filter(c => !c.is_premium_user).length,
      });
    } catch (error) {
      console.error('Error fetching calendars:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendars();
  }, [sortBy, sortDesc]);

  const handleSort = (column: 'created_at' | 'view_count' | 'download_count') => {
    if (sortBy === column) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(column);
      setSortDesc(true);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue }: { icon: LucideIcon; label: string; value: number | string; subValue?: string }) => (
    <Card className="bg-card border">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
            {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground">
          Track all shared calendars created by users
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCalendars}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Calendar} label="Total Calendars" value={stats.totalCalendars} />
        <StatCard icon={Users} label="Unique Creators" value={stats.uniqueCreators} />
        <StatCard icon={Eye} label="Total Views" value={stats.totalViews} />
        <StatCard icon={Download} label="Total Downloads" value={stats.totalDownloads} />
      </div>

      {/* Premium/Free breakdown */}
      <div className="flex gap-4 text-sm">
        <Badge variant="default" className="bg-amber-500/20 text-amber-600 border-amber-500/30">
          {stats.premiumCalendars} Premium
        </Badge>
        <Badge variant="secondary">
          {stats.freeCalendars} Free
        </Badge>
      </div>

      {/* Calendar Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Location</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Creator</TableHead>
              <TableHead>Type</TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted"
                onClick={() => handleSort('view_count')}
              >
                Views {sortBy === 'view_count' && (sortDesc ? '↓' : '↑')}
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted"
                onClick={() => handleSort('download_count')}
              >
                Downloads {sortBy === 'download_count' && (sortDesc ? '↓' : '↑')}
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted"
                onClick={() => handleSort('created_at')}
              >
                Created {sortBy === 'created_at' && (sortDesc ? '↓' : '↑')}
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Loading calendars...
                </TableCell>
              </TableRow>
            ) : calendars.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No calendars created yet
                </TableCell>
              </TableRow>
            ) : (
              calendars.map((calendar) => (
                <TableRow key={calendar.id}>
                  <TableCell className="font-medium">
                    {calendar.location_name}
                    <div className="text-xs text-muted-foreground">
                      {calendar.latitude.toFixed(2)}, {calendar.longitude.toFixed(2)}
                    </div>
                  </TableCell>
                  <TableCell>{calendar.year}</TableCell>
                  <TableCell className="text-sm">{calendar.email}</TableCell>
                  <TableCell>
                    {calendar.is_premium_user ? (
                      <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
                        Premium
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Free</Badge>
                    )}
                  </TableCell>
                  <TableCell>{calendar.view_count || 0}</TableCell>
                  <TableCell>{calendar.download_count || 0}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(calendar.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                    >
                      <a 
                        href={`/calendar/${calendar.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
